import React, { useEffect, useMemo, useState } from 'react';
import PageShell from './PageShell';
import DialogAlert from './components/DialogAlert';
import DialogFooter from './components/DialogFooter';
import type { SsoCredentials } from './types/index';

type MarketLike = { id: number; code?: string; name?: string };

const LEVEL_LABELS: Record<string, string> = {
  user: 'your sign-in',
  placeholder: 'your sign-in',
  facility: 'shared by a user in your organization',
  dayone: 'saved for your organization',
};

function MetrcSsoAuthDialog({
  markets = [],
  busy = false,
  credentials = null,
  onClose,
  onContinue,
  onUseOwnCredentials,
  onMarketChange,
}: {
  markets?: MarketLike[];
  busy?: boolean;
  credentials?: SsoCredentials | null;
  onClose: () => void;
  onContinue: (market: MarketLike) => void;
  onUseOwnCredentials?: (market: MarketLike) => void;
  onMarketChange?: (market: MarketLike | null) => void;
}) {
  const marketOptions = useMemo(() => {
    return (markets || [])
      .filter((m) => m && (m.code || m.name))
      .map((m) => ({
        value: String(m.id),
        label: m.code && m.name ? `${m.code} — ${m.name}` : String(m.code || m.name),
        market: m,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }, [markets]);

  const [marketId, setMarketId] = useState(marketOptions[0]?.value || '');
  const [errors, setErrors] = useState<Record<string, any>>({});

  useEffect(() => {
    if (!marketOptions.length) {
      if (marketId) setMarketId('');
      return;
    }

    const exists = marketOptions.some((o) => o.value === marketId);
    if (!exists) setMarketId(marketOptions[0].value);
  }, [marketOptions]);

  const selectedOption = marketOptions.find((o) => o.value === marketId);

  useEffect(() => {
    onMarketChange?.(selectedOption?.market || null);
  }, [selectedOption?.market]);

  const handleContinue = () => {
    if (!selectedOption) {
      setErrors({ market: 'Market is required' });
      return;
    }
    setErrors({});
    onContinue(selectedOption.market);
  };

  const handleUseOwnCredentials = () => {
    if (!selectedOption) {
      setErrors({ market: 'Market is required' });
      return;
    }
    setErrors({});
    onUseOwnCredentials?.(selectedOption.market);
  };

  const hasUsableCredentials = credentials?.ssoEnabled === true && credentials.requiresSsoAuthentication === false;
  const needsSignIn = credentials?.ssoEnabled === true && credentials.requiresSsoAuthentication === true;

  const footer = (
    <DialogFooter>
      {hasUsableCredentials && onUseOwnCredentials && (
        <button
          className="button secondary"
          onClick={handleUseOwnCredentials}
          disabled={busy}
          data-testid="metrc-sso-auth-own-credentials"
        >
          Use my own credentials
        </button>
      )}
      <button
        className="button"
        onClick={handleContinue}
        disabled={busy}
        data-testid="metrc-sso-auth-submit"
      >
        {busy ? 'Redirecting…' : 'Continue'}
      </button>
    </DialogFooter>
  );

  return (
    <PageShell
      contextLabel="Integrations"
      title="Authenticate"
      onClose={onClose}
      framed={false}
      testId="metrc-sso-auth"
      preview={(
        <div className="card" style={{ padding: 16 }}>
          <div className="muted" style={{ fontSize: 12, fontWeight: 800, marginBottom: 8 }}>Preview</div>
          <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 8 }}>Metrc Connection</div>
          <div className="muted" style={{ fontSize: 13, display: 'grid', gap: 6 }}>
            <div><span style={{ fontWeight: 700, color: 'var(--primary)' }}>Market:</span> {selectedOption?.label || '—'}</div>
          </div>
          <div className="divider" style={{ margin: '12px 0' }} />
          <div className="muted" style={{ fontSize: 12 }}>You&apos;ll be redirected to sign in for the selected market.</div>
        </div>
      )}
      alert={<DialogAlert show={Object.keys(errors).length > 0} />}
      footer={footer}
    >
      <div className="form-grid">
        <div className="field full-span">
          <label className="label">Market <span className="required-dot" aria-label="required">*</span></label>
          <select
            className={`select ${errors.market ? 'error' : ''}`}
            value={marketId}
            onChange={(e) => setMarketId(e.target.value)}
            data-testid="metrc-sso-auth-market"
          >
            <option value="">Select market</option>
            {marketOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {errors.market && <div className="error-text">{errors.market}</div>}
        </div>

        {hasUsableCredentials && (
          <div className="field full-span" data-testid="metrc-sso-auth-identity">
            <div className="muted" style={{ fontSize: 13 }}>
              Using the Metrc sign-in of{' '}
              <strong>{credentials?.mcaEmail || 'a saved Metrc account'}</strong>
              {credentials?.level && LEVEL_LABELS[credentials.level] ? ` (${LEVEL_LABELS[credentials.level]})` : ''}
            </div>
          </div>
        )}

        {needsSignIn && (
          <div className="field full-span" data-testid="metrc-sso-auth-no-credentials">
            <div className="muted" style={{ fontSize: 13 }}>
              No saved Metrc sign-in is available for this market. Continue to sign in with your Metrc account,
              or ask an organization admin who has a Metrc account to sign in.
            </div>
          </div>
        )}
      </div>
    </PageShell>
  );
}

export default React.memo(MetrcSsoAuthDialog);
